/**
 * Fuzzy matching utility for project name matching
 * Uses Levenshtein distance algorithm
 */

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;

  // Create distance matrix
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  // Initialize first row and column
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  // Fill in the rest of the matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1, // deletion
        dp[i][j - 1] + 1, // insertion
        dp[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return dp[m][n];
}

/**
 * Calculate similarity score between two strings (0-1)
 * 1 = identical, 0 = completely different
 */
export function calculateSimilarity(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();

  if (s1 === s2) return 1;
  if (s1.length === 0 || s2.length === 0) return 0;

  const distance = levenshteinDistance(s1, s2);
  const maxLength = Math.max(s1.length, s2.length);

  return 1 - distance / maxLength;
}

/**
 * Find the best matching string from a list of candidates
 * @param input The string to match
 * @param candidates List of possible matches
 * @param threshold Minimum similarity score to consider a match (default 0.5)
 * @returns The best match or null if no good match found
 */
export function findBestMatch(
  input: string,
  candidates: string[],
  threshold: number = 0.5
): { match: string; score: number; index: number } | null {
  if (!input || candidates.length === 0) return null;

  let bestMatch: { match: string; score: number; index: number } | null = null;

  candidates.forEach((candidate, index) => {
    const score = calculateSimilarity(input, candidate);

    if (score >= threshold && (!bestMatch || score > bestMatch.score)) {
      bestMatch = { match: candidate, score, index };
    }
  });

  return bestMatch;
}

/**
 * Find all matches above a threshold, sorted by score
 */
export function findAllMatches(
  input: string,
  candidates: string[],
  threshold: number = 0.3
): { match: string; score: number; index: number }[] {
  if (!input || candidates.length === 0) return [];

  const matches = candidates
    .map((candidate, index) => ({
      match: candidate,
      score: calculateSimilarity(input, candidate),
      index,
    }))
    .filter((m) => m.score >= threshold)
    .sort((a, b) => b.score - a.score);

  return matches;
}

/**
 * Check if a string is an exact match (case-insensitive)
 */
export function isExactMatch(input: string, candidate: string): boolean {
  return input.toLowerCase().trim() === candidate.toLowerCase().trim();
}
